/**
 * Module dependencies,
 */

import { DescriptiveSection } from 'src/components/layout/descriptive-section';
import { GetStaticProps, NextPage } from 'next';
import { Header } from 'src/components/layout/header';
import { HomePageContent } from 'src/types/page-content';
import { ImageGrid } from 'src/components/layout/section/image-grid';
import { Locale } from 'i18n-routes';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { TeamMember } from 'src/types/team-member';
import { TeamSection } from 'src/components/layout/section/team';
import { Testimonial } from 'src/types/testimonial';
import { Testimonials } from 'src/components/layout/section/testimonials';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { getHomePage } from 'src/requests/home';
import { getTeam } from 'src/requests/team';
import { getTestimonials } from 'src/requests/testimonials';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  data: {
    content: HomePageContent;
    teamMembers: TeamMember[];
    testimonials: Testimonial[];
  };
};

/**
 * `WhoWeAre` page.
 */

const WhoWeAre: NextPage<Props> = ({ data }) => {
  const { t } = useTranslation(['common', 'who-we-are']);
  const resolveRoute = useRouteResolver();

  return (
    <>
      <SEO />

      <TriggerNavbarTheme theme={'dark'} />

      <Header
        imageAlt={t('who-we-are:a11y.imageDescription') as string}
        imageUrl={'/images/eye.jpg'}
        lead={data.content.anteTitle}
        title={data.content.title}
      />

      <TriggerNavbarTheme theme={'light'} />

      <DescriptiveSection
        action={{
          label: t('who-we-are:buttonLabel'),
          url: resolveRoute('whatWeDo')
        }}
        animationDelay={200}
        content={[
          {
            description: t('who-we-are:content.firstParagraph'),
            lead: t('common:terms.clinic'),
            title: t('who-we-are:title')
          },
          {
            description: t('who-we-are:content.secondParagraph'),
            title: t('who-we-are:content.secondTitle')
          },
          {
            description: t('who-we-are:content.thirdParagraph'),
            title: t('who-we-are:content.thirdTitle')
          }
        ]}
      />

      <TriggerNavbarTheme theme={'dark'} />

      <TeamSection members={data.teamMembers} />

      <TriggerNavbarTheme theme={'light'} />

      <Testimonials items={data.testimonials} />

      <ImageGrid
        centerImageUrl={'/images/hand.jpg'}
        leftImageUrl={'/images/beach-2.jpg'}
        rightImageUrl={'/images/sand.jpg'}
      />
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({ locale }) => {
  const [content, teamMembers, testimonials] = await Promise.all([
    getHomePage(locale as Locale),
    getTeam(locale as Locale),
    getTestimonials(locale as Locale)
  ]);

  return {
    props: {
      data: { content, teamMembers, testimonials },
      navbarTheme: 'dark',
      ...(await serverSideTranslations(locale as Locale, [
        'common',
        'home',
        'who-we-are'
      ]))
    },
    revalidate: 60
  };
};

/**
 * Export `WhoWeAre` component.
 */

export default WhoWeAre;
